import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, CalendarDays, UserCog, LogOut, Sparkles, FolderKanban, Megaphone, Wrench, Inbox, Handshake, Menu, KeyRound, MessageSquareText, IndianRupee } from "lucide-react";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import NotificationBell from "./NotificationBell";
import { useConfirm } from "./ConfirmProvider";

const SECTIONS = [
  {
    key: "platform",
    label: "Platform",
    icon: LayoutDashboard,
    items: [
      { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview", roles: "*" },
      { to: "/my-work", label: "My Work", icon: Inbox, testid: "nav-my-work", roles: "*" },
    ],
  },
  {
    key: "smm",
    label: "Social Media",
    icon: Megaphone,
    items: [
      { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", roles: ["admin", "ceo", "coo", "manager", "account_manager", "graphic_designer", "content_writer", "execution_specialist", "sales_head", "sales_member"] },
      { to: "/calendar", label: "Content Calendar", icon: CalendarDays, testid: "nav-calendar", roles: ["admin", "ceo", "coo", "manager", "account_manager", "graphic_designer", "content_writer", "execution_specialist"] },
    ],
  },
  {
    key: "dev",
    label: "Development",
    icon: Wrench,
    items: [
      { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects", roles: ["admin", "ceo", "coo", "manager", "project_manager", "developer", "tester"] },
      { to: "/standups", label: "Standups", icon: MessageSquareText, testid: "nav-standups", roles: ["admin", "ceo", "coo", "manager", "project_manager"] },
      { to: "/project-costs", label: "Project Costs", icon: IndianRupee, testid: "nav-project-costs", roles: ["admin", "ceo", "coo"] },
    ],
  },
  {
    key: "sales",
    label: "Sales",
    icon: Handshake,
    items: [
      { to: "/leads", label: "Leads", icon: Handshake, testid: "nav-leads", roles: ["admin", "ceo", "coo", "manager", "sales_head", "sales_member"] },
    ],
  },
  {
    key: "org",
    label: "Organisation",
    icon: UserCog,
    items: [
      { to: "/team", label: "Team", icon: UserCog, testid: "nav-team", roles: ["admin", "ceo", "coo", "manager"] },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const onLogout = async () => {
    const ok = await confirm({ title: "Are you sure you want to logout?", description: "You will need to sign in again to continue.", confirmText: "Logout", destructive: false });
    if (!ok) return;
    await logout();
    navigate("/login");
  };
  const initials = (user?.name || "?").split(" ").map(x=>x[0]).slice(0,2).join("").toUpperCase();

  const visibleSections = SECTIONS
    .map(s => ({
      ...s,
      items: s.items.filter(n => n.roles === "*" || n.roles.includes(user?.role)),
    }))
    .filter(s => s.items.length > 0);

  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const brand = (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 grid place-items-center">
        <Sparkles className="w-4 h-4" />
      </div>
      <div>
        <div className="text-sm font-bold tracking-tight">Shloka<span className="text-orange-400">SMM</span></div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Agency OS</div>
      </div>
    </div>
  );

  const sidebar = (
    <>
        <div className="px-6 py-5 border-b border-[#E4E7EC]">{brand}</div>

        <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
          {visibleSections.map((section) => (
            <div key={section.key} data-testid={`nav-section-${section.key}`}>
              <div className="px-3 mb-1.5 flex items-center gap-2 text-[9px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                <section.icon className="w-3 h-3 text-orange-400/70" />
                {section.label}
              </div>
              <div className="space-y-1">
                {section.items.map(n => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={n.testid}
                    className={({isActive}) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive ? "bg-orange-100 text-orange-700 border border-orange-500/30" : "text-neutral-600 hover:text-neutral-900 hover:bg-black/[0.04] border border-transparent"
                      }`
                    }
                  >
                    <n.icon className="w-4 h-4" />
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-[#E4E7EC]">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 grid place-items-center text-[11px] font-bold shrink-0" data-testid="user-avatar">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase text-neutral-500 truncate">{user?.role}</div>
            </div>
            <NotificationBell />
            <Button size="icon" variant="ghost" onClick={() => setPwOpen(true)} title="Change password" data-testid="change-password-btn">
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={onLogout} title="Logout" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F5F7FB] text-[#0F1729] relative">
      <aside className="hidden md:flex w-64 shrink-0 border-r border-[#E4E7EC] bg-[#FFFFFF]/80 backdrop-blur-md sticky top-0 h-screen flex-col z-10">
        {!menuOpen && sidebar}
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col bg-white" data-testid="mobile-nav-sheet">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {menuOpen && sidebar}
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0 relative z-[2]">
        <header className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4 py-3 bg-white/90 backdrop-blur-md border-b border-[#E4E7EC]" data-testid="mobile-topbar">
          <Button size="icon" variant="ghost" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </Button>
          {brand}
          <div className="w-9" />
        </header>
        <div className="grid-lines absolute inset-0 opacity-30 pointer-events-none" />
        <div className="relative">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
